import {
  createContext,
  Reducer,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react'

interface AppContextState {
  isSideBarOpen: boolean
  isChatWidgetOpen: boolean
  selectedWorkerId?: string
  isReactNativeApp: boolean
}

interface BasicActions {
  type: 'SHOW_SIDEBAR' | 'HIDE_SIDEBAR'
}

interface ChatWidgetAction {
  type: 'SHOW_CHAT_WIDGET' | 'HIDE_CHAT_WIDGET'
  payload: { workerId?: string }
}

type AppContextAction = BasicActions | ChatWidgetAction

export type AppContextDispatch = React.Dispatch<AppContextAction>
export type AppContextReducer = Reducer<AppContextState, AppContextAction>

const initialState: AppContextState = {
  isSideBarOpen: false,
  isChatWidgetOpen: false,
  isReactNativeApp: !!window.ReactNativeWebView,
}

function reducer(
  state: AppContextState,
  action: AppContextAction,
): AppContextState {
  switch (action.type) {
    case 'SHOW_SIDEBAR':
      return {
        ...state,
        isSideBarOpen: true,
      }
    case 'HIDE_SIDEBAR':
      return {
        ...state,
        isSideBarOpen: false,
      }
    case 'SHOW_CHAT_WIDGET':
      return {
        ...state,
        isChatWidgetOpen: true,
        selectedWorkerId: action.payload.workerId,
      }
    case 'HIDE_CHAT_WIDGET':
      return {
        ...state,
        isChatWidgetOpen: false,
        selectedWorkerId: undefined,
      }
    default:
      return state
  }
}

const AppContext = createContext<{
  state: AppContextState
  dispatch: AppContextDispatch
  setIsSideBarOpen: (value: boolean) => void
  setIsChatWidgetOpen: (value: boolean, workerId?: string) => void
}>({
  state: initialState,
  dispatch: () => null,
  setIsSideBarOpen: () => null,
  setIsChatWidgetOpen: () => null,
})

export function useAppContext() {
  return useContext(AppContext)
}

export function AppContextProvider({ children }: { children: JSX.Element }) {
  const [state, dispatch] = useReducer<AppContextReducer>(reducer, initialState)
  const setIsSideBarOpen = useCallback(
    (value: boolean) =>
      value
        ? dispatch({ type: 'SHOW_SIDEBAR' })
        : dispatch({ type: 'HIDE_SIDEBAR' }),
    [],
  )
  const setIsChatWidgetOpen = useCallback(
    (value: boolean, workerId?: string) => {
      return value
        ? dispatch({ type: 'SHOW_CHAT_WIDGET', payload: { workerId } })
        : dispatch({ type: 'HIDE_CHAT_WIDGET', payload: { workerId } })
    },
    [],
  )

  const exportedContext = useMemo(
    () => ({
      state,
      dispatch,
      setIsSideBarOpen,
      setIsChatWidgetOpen,
    }),
    [state, dispatch, setIsSideBarOpen, setIsChatWidgetOpen],
  )

  return (
    <AppContext.Provider value={exportedContext}>
      {children}
    </AppContext.Provider>
  )
}
